import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {Alert, Box, Button, CircularProgress, IconButton, LinearProgress, Sheet, Skeleton, Stack, Typography} from '@mui/joy';
import ImageViewer from '../storage/ImageViewer';
import {isImage} from '../storage/images';
import FolderRounded from '@mui/icons-material/FolderRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import GridViewRounded from '@mui/icons-material/GridViewRounded';
import ViewListRounded from '@mui/icons-material/ViewListRounded';
import {runArchive, archiveStatus} from './archive';
import './eventMaterials.css';
import {API_URL} from '../api';
import {contentFingerprint, MAX_FILE_SIZE, requestJSON, resumableRequestId, resolveMediaURL, safeMediaURL, uploadDirectFile} from './direct';

const labels = {input: 'Вводные данные', measurement: 'Материалы обмера', sheets: 'Обмерные листы',
  photos: 'Фото и видео', drawings: 'Рабочие чертежи', result: 'Готовые чертежи', published: 'Выданные комплекты'};
const sizeLabel = size => size < 1024 ? `${size} Б` : size < 1024 ** 2 ? `${(size / 1024).toFixed(1)} КБ` : `${(size / 1024 ** 2).toFixed(1)} МБ`;

function PhotoThumbnail({node, resolveSource}) {
  const element = useRef(null), current = useRef(node);
  current.current = node;
  const [visible, setVisible] = useState(false), [url, setURL] = useState(''), [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!window.IntersectionObserver) {setVisible(true); return undefined;}
    const observer = new window.IntersectionObserver(entries => {if (entries.some(entry => entry.isIntersecting)) {setVisible(true); observer.disconnect();}}, {rootMargin: '180px'});
    if (element.current) observer.observe(element.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return undefined;
    const controller = new AbortController(); setURL(''); setFailed(false);
    resolveSource(current.current, controller.signal).then(value => {if (!controller.signal.aborted) setURL(value);})
      .catch(() => {if (!controller.signal.aborted) setFailed(true);});
    return () => controller.abort();
  }, [visible, node.id, node.version, resolveSource]);
  return <span ref={element} className="em-photo" aria-hidden="true">{url && !failed
    ? <img src={url} alt="" loading="lazy" decoding="async" crossOrigin="anonymous" referrerPolicy="no-referrer" onError={() => setFailed(true)}/>
    : <ImageRounded/>}</span>;
}
function storedView() {try {return localStorage.getItem('planium-staff-event-media-view') === 'list' ? 'list' : 'grid';} catch {return 'grid';}}

export default function HomeMediaFolder({audience = 'staff', getToken, backPath}) {
  const {objectId, section: routeSection} = useParams();
  const navigate = useNavigate();
  const section = routeSection || (audience === 'staff' ? 'measurement' : 'result');
  const scope = `${audience}:${objectId}:${section}`;
  const token = getToken ? getToken() : localStorage.getItem('client_token');
  const allowed = audience === 'staff'
    ? ['input', 'measurement', 'sheets', 'photos', 'drawings', 'published'] : ['input', 'measurement', 'sheets', 'photos', 'result'];
  const validRoute = /^[1-9]\d*$/.test(String(objectId)) && allowed.includes(section);
  const prefix = `/main/api/media/${audience}/${audience === 'staff' ? 'events' : 'applications'}/${objectId}/${section}/`;
  const [view, setView] = useState(storedView);
  const [selected, setSelected] = useState(() => new Set()), [archiveJob, setArchiveJob] = useState(null), [archiving, setArchiving] = useState(false);
  const archiveController = useRef(null);
  useEffect(() => {try {localStorage.setItem('planium-staff-event-media-view', view);} catch { /* Browser storage unavailable. */ }}, [view]);
  const [selection, setSelection] = useState({scope, id: ''});
  const folder = selection.scope === scope ? selection.id : '';
  const [result, setResult] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeFile, setActiveFile] = useState('');
  const [hint, setHint] = useState('');
  const [progress, setProgress] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const uploadController = useRef(null), lifecycle = useRef(0);
  const data = result?.scope === scope && result?.folder === folder && result?.token === token ? result.value : null;

  useEffect(() => {setSelected(new Set());setArchiveJob(null);setArchiving(false);archiveController.current?.abort();return () => archiveController.current?.abort();}, [scope, folder, token]);

  useEffect(() => {
    setBusy(false); setHint(''); setActiveFile(''); setProgress(0); setViewer(null);
    lifecycle.current += 1;
    return () => { lifecycle.current += 1; uploadController.current?.abort(); };
  }, [scope, token]);

  useEffect(() => {
    if (!busy) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  useEffect(() => {
    const controller = new AbortController();
    setError(''); setLoading(true);
    if (!validRoute || !token) {
      setError(!validRoute ? 'Некорректная ссылка на папку.' : 'Войдите в свой кабинет, чтобы открыть материалы.');
      setLoading(false);
      return () => controller.abort();
    }
    const url = API_URL + prefix + (folder ? `?folder=${encodeURIComponent(folder)}` : '');
    requestJSON(url, {headers: {Authorization: `${audience === 'staff' ? 'Bearer' : 'ClientToken'} ${token}`}, cache: 'no-store'}, controller.signal)
      .then(value => {
        if (!Array.isArray(value.items) || !Array.isArray(value.rights) || !Array.isArray(value.breadcrumbs)) throw new Error('Сервер вернул некорректный список файлов.');
        if (!controller.signal.aborted) setResult({scope, folder, token, value});
      })
      .catch(failure => { if (!controller.signal.aborted) {setError(failure.message || 'Не удалось открыть папку. Повторите позже.'); if ([401,403,404].includes(failure.status)) {setResult(null);setViewer(null);setSelected(new Set());}} })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [scope, prefix, folder, refresh, token, audience, validRoute]);

  const pendingTransfer = data?.items.some(node => node.transfer_state === 'pending');
  useEffect(() => {
    if (!pendingTransfer || busy) return undefined;
    const timer = window.setInterval(() => {if (!document.hidden) setRefresh(value => value + 1);}, 20000);
    return () => window.clearInterval(timer);
  }, [pendingTransfer, busy, scope, folder]);

  const api = (path, body, signal) => requestJSON(API_URL + path, {method: 'POST',
    headers: {Authorization: `${audience === 'staff' ? 'Bearer' : 'ClientToken'} ${token}`, 'Content-Type': 'application/json'},
    body: JSON.stringify(body)}, signal);
  const resolveSource = useCallback(async (node, signal) => {
    const ticket = await requestJSON(API_URL + prefix + `files/${node.id}/download/`, {method: 'POST',
      headers: {Authorization: `${audience === 'staff' ? 'Bearer' : 'ClientToken'} ${token}`, 'Content-Type': 'application/json'},
      body: JSON.stringify({preview: true})}, signal);
    return resolveMediaURL(API_URL, ticket.url, {preview: true, signal});
  }, [prefix, token, audience]);
  const downloadFile = useCallback(async (node, signal) => {
    const ticket = await requestJSON(API_URL + prefix + `files/${node.id}/download/`, {method: 'POST',
      headers: {Authorization: `${audience === 'staff' ? 'Bearer' : 'ClientToken'} ${token}`, 'Content-Type': 'application/json'},
      body: JSON.stringify({})}, signal);
    if (signal?.aborted) return;
    const anchor = document.createElement('a'); anchor.href = safeMediaURL(ticket.url).href;
    anchor.rel = 'noreferrer noopener'; anchor.referrerPolicy = 'no-referrer';
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
  }, [prefix, token, audience]);
  async function openFile(node, preview = false) {
    if (preview) {
      setViewer({startId: node.id, images: data.items.filter(node => isImage(node) && data.rights.includes('download') && (!node.rights || node.rights.includes('download')))});
      return;
    }
    const generation = lifecycle.current;
    setError(''); setActiveFile(node.id);
    try {await downloadFile(node);}
    catch (failure) {if (lifecycle.current === generation) setError(failure.message || 'Не удалось получить файл.');}
    finally {if (lifecycle.current === generation) setActiveFile('');}
  }
  async function remove(node) {
    if (!window.confirm('Убрать файл из рабочего комплекта? Оригинал сохранится в хранилище.')) return;
    const generation = lifecycle.current;
    setError(''); setActiveFile(node.id);
    try {
      await api(prefix + 'mutate/', {action: 'delete', node: node.id, revision: node.revision});
      if (lifecycle.current === generation) setRefresh(value => value + 1);
    } catch (failure) { if (lifecycle.current === generation) setError(failure.message); }
    finally { if (lifecycle.current === generation) setActiveFile(''); }
  }
  async function upload(files) {
    if (!files.length || uploadController.current) return;
    if (files.length > 500 || files.some(file => !file.size || file.size > MAX_FILE_SIZE)) {
      setError('Выберите не больше 500 непустых файлов, каждый не больше 2 ГиБ.'); return;
    }
    // Freeze the destination for this batch; a later folder navigation must
    // neither redirect remaining files nor reuse another folder's upload ID.
    const targetFolder = data?.folder?.id || data?.root_id;
    if (!targetFolder) { setError('Обновите папку перед загрузкой файлов.'); return; }
    const controller = new AbortController(), generation = lifecycle.current;
    uploadController.current = controller;
    const update = fn => { if (lifecycle.current === generation) fn(); };
    setBusy(true); setError(''); setProgress(0);
    let saved = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        update(() => setHint(`Подготавливаем ${i + 1}/${files.length}: ${file.name}`));
        const fingerprint = await contentFingerprint(file, `${scope}:folder:${targetFolder}:${token}`, controller.signal,
          percent => update(() => setHint(`Подготовка ${file.name}: ${percent}%`)));
        const id = resumableRequestId(fingerprint);
        await uploadDirectFile(API_URL, {file, token, signal: controller.signal,
          initPath: '/main/api/media/direct/init/',
          initData: {request_id: id, event_id: Number(objectId), section, target_folder: targetFolder,
            file_name: file.name, total_size: file.size},
          completePath: identity => `/main/api/media/direct/${identity}/commit/`,
          onProgress: value => update(() => {
            setProgress(Math.floor((i * 100 + value.percent) / files.length));
            setHint(`Файл ${i + 1}/${files.length} · ${file.name}: ${value.percent}%`);
          }),
          onRetry: value => update(() => setHint(value.message)),
        });
        saved += 1;
      }
      update(() => { setProgress(100); setHint(`Сохранено файлов: ${saved}`); });
    } catch (failure) {
      update(() => setError(failure.name === 'AbortError'
        ? `Загрузка приостановлена. Сохранено: ${saved}. Для продолжения выберите те же файлы.`
        : (failure.message || 'Не удалось загрузить файлы.')));
    } finally {
      uploadController.current = null;
      update(() => { setBusy(false); setRefresh(value => value + 1); });
    }
  }
  const tabs = audience === 'staff' ? ['input', 'measurement', 'drawings'] : ['input', 'measurement', 'result'];
  const route = value => audience === 'staff' ? `/storage/events/${objectId}/${value}` : `/lk/storage/${objectId}/${value}`;
  const atRoot = data && (!folder || folder === data.root_id);
  const canAdd = audience === 'staff' && ['input', 'drawings'].includes(section) && data?.rights.includes('add');
  const canDownload = node => data?.rights.includes('download') && (!node.rights || node.rights.includes('download'));
  const selectable = node => node.kind === 'folder' ? data?.rights.includes('read') : canDownload(node);
  const eligible = data?.items.filter(selectable) || [];
  useEffect(() => {
    if (!data) return;
    const visible = new Set(data.items.filter(node => node.kind === 'folder' ? data.rights.includes('read') : data.rights.includes('download') && (!node.rights || node.rights.includes('download'))).map(node => node.id));
    setSelected(previous => {const next = new Set([...previous].filter(id => visible.has(id))); return next.size === previous.size ? previous : next;});
  }, [data]);
  const toggle = id => setSelected(previous => {const next = new Set(previous); if (next.has(id)) next.delete(id); else next.add(id); return next;});
  async function archive(ids) {
    if (!ids.length || archiveController.current) return;
    const controller = new AbortController(), generation = lifecycle.current;
    archiveController.current = controller; setArchiving(true); setArchiveJob(null); setError('');
    try {await runArchive(API_URL, token, {scope: audience, object_id: Number(objectId), section, ids}, {
      signal: controller.signal, audience, onUpdate: value => {if (!controller.signal.aborted && generation === lifecycle.current) setArchiveJob(value);}});}
    catch (failure) {if (!controller.signal.aborted && generation === lifecycle.current) {setError(failure.message);if ([401,403,404].includes(failure.status)) {setResult(null);setSelected(new Set());setViewer(null);}}}
    finally {if (archiveController.current === controller) archiveController.current = null; if (!controller.signal.aborted && generation === lifecycle.current) setArchiving(false);}
  }
  const open = node => {
    if (node.kind === 'folder') {setSelection({scope, id: node.id}); return;}
    if (!canDownload(node)) return;
    openFile(node, isImage(node));
  };
  return <Box component="main" className="em-page">
    <Button className="em-back" variant="outlined" color="neutral" size="sm" disabled={busy} startDecorator={<ArrowBackRounded/>}
      onClick={() => navigate(backPath || (audience === 'staff' ? '/storage' : '/lk'))}>Вернуться {audience === 'staff' ? 'в хранилище' : 'в кабинет'}</Button>
    <header className="em-heading"><div><Typography className="em-eyebrow" level="body-sm">Материалы {audience === 'staff' ? 'события' : 'заявки'} №{objectId}</Typography>
      <Typography component="h1" level="h2">{labels[section] || 'Материалы'}</Typography>
      <Typography level="body-sm" className="em-subtitle">Фотографии, документы и исходные файлы</Typography></div>
      <span className="em-private"><FolderRounded/>Файлы события</span></header>
    <Sheet variant="outlined" className="em-workspace">
      <div className="em-toolbar"><nav className="em-tabs" aria-label="Разделы материалов">
        {tabs.map(value => <Button key={value} size="sm" disabled={busy} variant={value === section ? 'soft' : 'plain'} color={value === section ? 'primary' : 'neutral'}
          aria-current={value === section ? 'page' : undefined} onClick={() => navigate(route(value))}>{labels[value]}</Button>)}
      </nav><div className="em-tools"><IconButton size="sm" variant="plain" color="neutral" disabled={busy || loading} aria-label="Обновить файлы" onClick={() => setRefresh(value => value + 1)}>
        {loading ? <CircularProgress size="sm"/> : <RefreshRounded/>}</IconButton><div className="em-view-switch" role="group" aria-label="Вид файлов">
        <IconButton size="sm" variant={view === 'grid' ? 'soft' : 'plain'} aria-label="Показать карточками" aria-pressed={view === 'grid'} onClick={() => setView('grid')}><GridViewRounded/></IconButton>
        <IconButton size="sm" variant={view === 'list' ? 'soft' : 'plain'} aria-label="Показать списком" aria-pressed={view === 'list'} onClick={() => setView('list')}><ViewListRounded/></IconButton>
      </div></div></div>
      <div className="em-content">
        {error && <Alert color="warning" role="alert" sx={{mb: 2, overflowWrap: 'anywhere'}}>{error}</Alert>}
        {busy && <Stack spacing={1} sx={{mb: 2}}><LinearProgress determinate value={progress} aria-label="Прогресс загрузки"/>
          <Typography level="body-sm" role="status">{hint}</Typography><Button size="sm" variant="outlined" color="neutral" onClick={() => uploadController.current?.abort()}>Приостановить загрузку</Button></Stack>}
        {!busy && hint && <Typography level="body-sm" role="status" sx={{mb: 2}}>{hint}</Typography>}
        <div className="em-path-row"><nav className="em-path" aria-label="Путь к папке">
          {data?.breadcrumbs.map((crumb, index) => <React.Fragment key={crumb.id}>{index > 0 && <ChevronRightRounded/>}
            {index === data.breadcrumbs.length - 1 ? <span aria-current="location">{crumb.name}</span>
              : <button type="button" disabled={busy} onClick={() => setSelection({scope, id: crumb.id})}>{crumb.name}</button>}</React.Fragment>)}
          {!data && <span>{labels[section] || 'Материалы'}</span>}
        </nav>{data && <Typography level="body-xs" className="em-count">Элементов: {data.items.length}</Typography>}</div>
        {data && <div className="em-selection"><label><input type="checkbox" checked={eligible.length > 0 && eligible.every(node => selected.has(node.id))}
          disabled={!eligible.length || archiving} aria-label="Выбрать все элементы" onChange={event => setSelected(event.target.checked ? new Set(eligible.map(node => node.id)) : new Set())}/>Выбрать всё</label>
          <span>{selected.size ? `Выбрано: ${selected.size}` : 'Файлы и папки можно скачать одним ZIP'}</span>
          {selected.size > 0 && <><Button size="sm" disabled={archiving} startDecorator={<DownloadRounded/>} onClick={() => archive([...selected])}>Скачать ZIP</Button>
            <Button size="sm" variant="plain" disabled={archiving} onClick={() => setSelected(new Set())}>Снять выбор</Button></>}
          {canAdd && atRoot && <Button component="label" size="sm" variant="soft" disabled={busy || Boolean(activeFile)}>Добавить файлы<input hidden type="file" multiple disabled={busy}
            onChange={event => {const files = Array.from(event.target.files || []); event.target.value = ''; upload(files);}}/></Button>}
        </div>}
        {(archiving || archiveJob) && <Alert color="neutral" variant="soft" sx={{mb: 2}} startDecorator={archiving ? <CircularProgress size="sm"/> : undefined}
          endDecorator={archiving ? <Button size="sm" variant="plain" onClick={() => {archiveController.current?.abort();archiveController.current = null;setArchiving(false);setArchiveJob(null);}}>Прекратить ожидание</Button> : undefined}>{archiveStatus(archiveJob)}</Alert>}
        {loading && data && <LinearProgress aria-label="Обновление файлов" className="em-refresh-progress"/>}
        {loading && !data && <div className="em-files is-grid" aria-label="Загрузка папки">{[1, 2, 3].map(item => <Skeleton key={item} variant="rectangular" height={190} sx={{borderRadius: '12px'}}/>)}</div>}
        {data && !!data.items.length && <div className={`em-files is-${view}`} aria-label="Файлы события">{data.items.map(node => <article key={node.id} className={`em-file ${node.kind === 'folder' ? 'is-folder' : ''} ${selected.has(node.id) ? 'is-selected' : ''}`}>
          {selectable(node) && <label className="em-file-check"><input type="checkbox" checked={selected.has(node.id)} disabled={archiving} aria-label={`Выбрать ${node.name}`} onChange={() => toggle(node.id)}/></label>}
          <button type="button" className="em-file-open" onClick={() => open(node)} disabled={busy || node.kind === 'file' && !canDownload(node)}
            aria-label={`${node.kind === 'folder' ? 'Открыть папку' : isImage(node) ? 'Открыть фото' : 'Скачать файл'} ${node.name}`}>
            <span className="em-file-visual">{node.kind === 'folder' ? <FolderRounded/> : isImage(node) && canDownload(node) && view === 'grid' && node.size <= 12 * 1024 ** 2
              ? <PhotoThumbnail node={node} resolveSource={resolveSource}/> : isImage(node) ? <ImageRounded/> : <InsertDriveFileRounded/>}</span>
            <span className="em-file-description"><strong title={node.name}>{node.name}</strong><span>{node.kind === 'folder' ? 'Папка' : sizeLabel(node.size || 0)}</span>
              {node.kind === 'folder' && <>{node.address && <span className="em-address">{node.address}</span>}<span>{node.owner_label || 'Система'}</span></>}
              {node.transfer_state === 'pending' && <span className="em-sync">Сохранён, ожидает переноса</span>}</span>
            {node.kind === 'folder' && <ChevronRightRounded className="em-folder-arrow"/>}</button>
          <div className="em-file-actions"><Typography level="body-xs">{node.kind === 'folder' ? 'Папка' : isImage(node) ? 'Изображение' : 'Файл'}</Typography>
            <div>{(node.kind === 'folder' || canDownload(node)) && <IconButton size="sm" variant="soft" color="primary" loading={activeFile === node.id} disabled={busy || Boolean(activeFile) || archiving}
              aria-label={`Скачать ${node.name}${node.kind === 'folder' ? ' ZIP' : ''}`} onClick={() => node.kind === 'folder' ? archive([node.id]) : openFile(node)}><DownloadRounded/></IconButton>}
            {audience === 'staff' && node.kind === 'file' && data.rights.includes('delete') && <IconButton size="sm" variant="plain" color="danger" aria-label={`Убрать ${node.name}`} title="Убрать файл" disabled={busy || Boolean(activeFile)} onClick={() => remove(node)}><DeleteOutlineRounded/></IconButton>}</div></div>
        </article>)}</div>}
        {data && !data.items.length && <div className="em-empty"><FolderRounded/><Typography level="title-md">В этой папке пока нет файлов</Typography><Typography level="body-sm">Здесь появятся материалы события</Typography></div>}
      </div>
    </Sheet>
    {viewer && <ImageViewer images={viewer.images} startId={viewer.startId} resolveSource={resolveSource} downloadFile={downloadFile} onClose={() => setViewer(null)}/>}
  </Box>;
}
